import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import AuthLayout, { Field, inputCls } from "@/pages/AuthLayout";
import { ROLE_LABEL } from "@/lib/content";

export default function Signup() {
  const { register, meta } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member", pillar: "", sub_sector: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pillars = meta?.pillars || [];
  const subSectors = pillars.find((p) => p.id === form.pillar)?.subSectors || [];
  const roles = (meta?.roles || []).filter((r) => r !== "admin");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Application received. Your dossier awaits.");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Request Clearance"
      subtitle="Begin your credentialing into the N360 sovereign network."
      footer={<>Already credentialed? <Link to="/login" className="text-[#F1551D] hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit}>
        <Field label="Full Name / Title">
          <input data-testid="signup-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="H.E. Dr. …" />
        </Field>
        <Field label="Institutional Email">
          <input data-testid="signup-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@institution.org" />
        </Field>
        <Field label="Passphrase (min 6 chars)">
          <input data-testid="signup-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className={inputCls} placeholder="••••••••" />
        </Field>
        <Field label="Primary Role">
          <select data-testid="signup-role-select" value={form.role} onChange={set("role")} className={inputCls}>
            {roles.map((r) => <option key={r} value={r} className="bg-[#F6EEDC]">{ROLE_LABEL[r] || r}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-1 gap-0">
          <Field label="Ecosystem Pillar">
            <select data-testid="signup-pillar-select" value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value, sub_sector: "" })} className={inputCls}>
              <option value="" className="bg-[#F6EEDC]">Select pillar…</option>
              {pillars.map((p) => <option key={p.id} value={p.id} className="bg-[#F6EEDC]">{p.name}</option>)}
            </select>
          </Field>
          {subSectors.length > 0 && (
            <Field label="Sub-Sector">
              <select data-testid="signup-subsector-select" value={form.sub_sector} onChange={set("sub_sector")} className={inputCls}>
                <option value="" className="bg-[#F6EEDC]">Select sub-sector…</option>
                {subSectors.map((s) => <option key={s} value={s} className="bg-[#F6EEDC]">{s}</option>)}
              </select>
            </Field>
          )}
        </div>
        <button data-testid="signup-submit-btn" disabled={loading} className="w-full py-2.5 rounded-lg btn-gold font-semibold disabled:opacity-60 mt-1">
          {loading ? "Submitting…" : "Request Clearance"}
        </button>
        <p className="text-[11px] text-[#8598a0] mt-3 text-center">New principals enter at Bronze tier · Level-2 (Member) access, pending sovereign verification.</p>
      </form>
    </AuthLayout>
  );
}
